import { useState, useEffect } from "react";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Smartphone, 
  CreditCard, 
  MapPin, 
  Plus, 
  RefreshCw, 
  Route,
  IndianRupee,
  Clock,
  Signal,
  Truck,
  PlayCircle,
  Eye,
  Zap,
  Search,
  Map,
  FileText,
  Car,
  Shield,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Building2,
  Calendar,
  User,
  Package,
  ArrowRight,
  Download,
  Navigation,
  Phone,
  MapPinned,
  CircleDot,
  Ban
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Toll plaza icon
const tollIcon = new L.DivIcon({
  className: "custom-toll-icon",
  html: `<div style="background: #f59e0b; border-radius: 50%; padding: 6px; border: 2px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2">
      <rect x="3" y="3" width="18" height="18" rx="2"/>
      <path d="M12 8v8M8 12h8"/>
    </svg>
  </div>`,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
});

// Map center component
const MapCenter = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, map.getZoom());
    }
  }, [center, map]);
  return null;
};

const SIMFastagTrackingPage = () => {
  const [activeTab, setActiveTab] = useState("sim");
  const [sims, setSims] = useState([]);
  const [fastags, setFastags] = useState([]);
  const [tollTransactions, setTollTransactions] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simDialogOpen, setSimDialogOpen] = useState(false);
  const [fastagDialogOpen, setFastagDialogOpen] = useState(false);
  const [journeyDialogOpen, setJourneyDialogOpen] = useState(false);
  const [selectedJourney, setSelectedJourney] = useState(null);
  const [mapCenter, setMapCenter] = useState([20.5937, 78.9629]);
  
  // Live FASTag Tracking states
  const [liveTrackingOpen, setLiveTrackingOpen] = useState(false);
  const [liveVehicleNumber, setLiveVehicleNumber] = useState("");
  const [liveTrackingData, setLiveTrackingData] = useState(null);
  const [liveTrackingLoading, setLiveTrackingLoading] = useState(false);

  // RC Verification states
  const [rcVehicleNumber, setRcVehicleNumber] = useState("");
  const [rcData, setRcData] = useState(null);
  const [rcLoading, setRcLoading] = useState(false);
  const [rcHistory, setRcHistory] = useState([]);

  // E-Way Bill Verification states
  const [ewbNumber, setEwbNumber] = useState("");
  const [ewbData, setEwbData] = useState(null);
  const [ewbLoading, setEwbLoading] = useState(false);
  const [ewbHistory, setEwbHistory] = useState([]);

  // FASTag Trip states
  const [fastagTrips, setFastagTrips] = useState([]);
  const [tripDialogOpen, setTripDialogOpen] = useState(false);
  const [tripLoading, setTripLoading] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [tripDetailDialogOpen, setTripDetailDialogOpen] = useState(false);
  const [tripForm, setTripForm] = useState({
    vehicle_number: "",
    driver_name: "",
    driver_phone: "",
    from_location: "",
    to_location: "",
    start_date: new Date().toISOString().split('T')[0],
    expected_end_date: "",
    expected_toll_amount: "",
    remarks: ""
  });

  // Forms
  const [simForm, setSimForm] = useState({
    vehicle_number: "",
    sim_number: "",
    imei_number: "",
    provider: "airtel",
    validity_date: ""
  });

  const [fastagForm, setFastagForm] = useState({
    vehicle_number: "",
    fastag_id: "",
    bank_name: "ICICI",
    vehicle_class: "VC10",
    linked_mobile: "",
    balance: 1000
  });

  useEffect(() => {
    fetchData();
    fetchRcHistory();
    fetchEwbHistory();
    fetchFastagTrips();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [simsRes, fastagsRes, txRes, trucksRes] = await Promise.all([
        axios.get(`${API_URL}/sim-tracking`),
        axios.get(`${API_URL}/fastag`),
        axios.get(`${API_URL}/fastag/toll-transactions?limit=50`),
        axios.get(`${API_URL}/trucks`)
      ]);
      setSims(simsRes.data);
      setFastags(fastagsRes.data);
      setTollTransactions(txRes.data);
      setTrucks(trucksRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch FASTag Trips
  const fetchFastagTrips = async () => {
    try {
      const response = await axios.get(`${API_URL}/fastag/trips?limit=50`);
      setFastagTrips(response.data);
    } catch (error) {
      console.error("Error fetching FASTag trips:", error);
    }
  };

  // Create FASTag Trip
  const handleCreateTrip = async (e) => {
    e.preventDefault();
    if (!tripForm.vehicle_number || !tripForm.from_location || !tripForm.to_location) {
      toast.error("Please fill all required fields");
      return;
    }
    
    setTripLoading(true);
    try {
      await axios.post(`${API_URL}/fastag/trips`, {
        ...tripForm,
        expected_toll_amount: tripForm.expected_toll_amount ? parseFloat(tripForm.expected_toll_amount) : 0
      });
      toast.success("Trip created successfully");
      setTripDialogOpen(false);
      setTripForm({
        vehicle_number: "",
        driver_name: "",
        driver_phone: "",
        from_location: "",
        to_location: "",
        start_date: new Date().toISOString().split('T')[0],
        expected_end_date: "",
        expected_toll_amount: "",
        remarks: ""
      });
      fetchFastagTrips();
    } catch (error) {
      console.error("Error creating trip:", error);
      toast.error("Failed to create trip");
    } finally {
      setTripLoading(false);
    }
  };

  // Update Trip Toll Data
  const handleUpdateTripToll = async (tripId) => {
    try {
      toast.loading("Fetching toll data...", { id: "toll-update" });
      const response = await axios.put(`${API_URL}/fastag/trips/${tripId}/update-toll`);
      if (response.data.success) {
        toast.success(`Found ${response.data.toll_crossings} toll crossings - ₹${response.data.total_toll}`, { id: "toll-update" });
        fetchFastagTrips();
        if (selectedTrip?.id === tripId) {
          setSelectedTrip({ ...selectedTrip, ...response.data });
        }
      } else {
        toast.error(response.data.message || "No toll data found", { id: "toll-update" });
      }
    } catch (error) {
      console.error("Error updating toll:", error);
      toast.error("Failed to fetch toll data", { id: "toll-update" });
    }
  };

  // Complete Trip
  const handleCompleteTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/fastag/trips/${tripId}/complete`);
      toast.success("Trip marked as completed");
      fetchFastagTrips();
      setTripDetailDialogOpen(false);
    } catch (error) {
      console.error("Error completing trip:", error);
      toast.error("Failed to complete trip");
    }
  };

  // Cancel Trip
  const handleCancelTrip = async (tripId) => {
    try {
      await axios.put(`${API_URL}/fastag/trips/${tripId}/cancel`);
      toast.success("Trip cancelled");
      fetchFastagTrips();
      setTripDetailDialogOpen(false);
    } catch (error) {
      console.error("Error cancelling trip:", error);
      toast.error("Failed to cancel trip");
    }
  };

  // View Trip Details
  const handleViewTrip = async (trip) => {
    setSelectedTrip(trip);
    setTripDetailDialogOpen(true);
  };

  // Live FASTag tracking via fastagtracking.com API
  const fetchLiveTracking = async (vehicleNum) => {
    if (!vehicleNum) {
      toast.error("Please enter a vehicle number");
      return;
    }
    
    setLiveTrackingLoading(true);
    setLiveTrackingData(null);
    
    try {
      const response = await axios.post(`${API_URL}/fastag/live-tracking?vehicle_number=${vehicleNum.toUpperCase()}`);
      
      if (response.data.success) {
        setLiveTrackingData(response.data);
        toast.success(`Found ${response.data.toll_count} toll transactions`);
        
        // Center map on first journey point
        if (response.data.journey_points?.length > 0) {
          const firstPoint = response.data.journey_points[0];
          setMapCenter([firstPoint.lat, firstPoint.lng]);
        }
      } else {
        toast.error(response.data.error || "No toll data found for this vehicle");
      }
    } catch (error) {
      console.error("Live tracking error:", error);
      toast.error(error.response?.data?.detail || "Failed to fetch live tracking data");
    } finally {
      setLiveTrackingLoading(false);
    }
  };

  // RC Verification
  const fetchRcHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/rc/history?limit=20`);
      setRcHistory(response.data);
    } catch (error) {
      console.error("Error fetching RC history:", error);
    }
  };

  const verifyRC = async () => {
    if (!rcVehicleNumber) {
      toast.error("Please enter a vehicle number");
      return;
    }
    
    setRcLoading(true);
    setRcData(null);
    
    try {
      const response = await axios.post(`${API_URL}/rc/verify?vehicle_number=${rcVehicleNumber.toUpperCase()}`);
      
      if (response.data.success) {
        setRcData(response.data);
        toast.success("RC details fetched successfully");
        fetchRcHistory(); // Refresh history
      } else {
        setRcData(response.data);
        toast.error(response.data.error || "No RC data found");
      }
    } catch (error) {
      console.error("RC verification error:", error);
      toast.error(error.response?.data?.detail || "Failed to verify RC");
    } finally {
      setRcLoading(false);
    }
  };

  // E-Way Bill Verification
  const fetchEwbHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/eway/history?limit=20`);
      setEwbHistory(response.data);
    } catch (error) {
      console.error("Error fetching E-Way Bill history:", error);
    }
  };

  const verifyEwayBill = async () => {
    if (!ewbNumber) {
      toast.error("Please enter an E-Way Bill number");
      return;
    }
    
    setEwbLoading(true);
    setEwbData(null);
    
    try {
      const response = await axios.post(`${API_URL}/eway/verify?ewb_number=${ewbNumber}`);
      
      if (response.data.success) {
        setEwbData(response.data);
        toast.success("E-Way Bill details fetched successfully");
        fetchEwbHistory(); // Refresh history
      } else {
        setEwbData(response.data);
        toast.error(response.data.error || "No E-Way Bill data found");
      }
    } catch (error) {
      console.error("E-Way Bill verification error:", error);
      toast.error(error.response?.data?.detail || "Failed to verify E-Way Bill");
    } finally {
      setEwbLoading(false);
    }
  };

  // PDF Download for RC History
  const downloadRcHistoryPDF = () => {
    if (rcHistory.length === 0) {
      toast.error("No RC verification history to download");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text("RC Verification History", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
    doc.text(`Total Records: ${rcHistory.length}`, 14, 34);

    // Table data
    const tableData = rcHistory.map((item, idx) => [
      idx + 1,
      item.vehicle_number || "N/A",
      item.verification_data?.owner_name || "N/A",
      item.verification_data?.vehicle_class || "N/A",
      item.verification_data?.fitness_upto || "N/A",
      item.verification_data?.insurance_upto || "N/A",
      item.verified_by || "N/A",
      new Date(item.verified_at).toLocaleDateString()
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["#", "Vehicle No.", "Owner", "Vehicle Class", "Fitness Upto", "Insurance Upto", "Verified By", "Date"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontSize: 8,
        fontStyle: "bold"
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [50, 50, 50]
      },
      alternateRowStyles: {
        fillColor: [245, 247, 250]
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 25 },
        2: { cellWidth: 35 },
        3: { cellWidth: 25 },
        4: { cellWidth: 22 },
        5: { cellWidth: 22 },
        6: { cellWidth: 25 },
        7: { cellWidth: 20 }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount} | Ardhanageshwar Logistics TMS`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    doc.save(`RC_Verification_History_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("RC History PDF downloaded successfully");
  };

  // PDF Download for E-Way Bill History
  const downloadEwbHistoryPDF = () => {
    if (ewbHistory.length === 0) {
      toast.error("No E-Way Bill verification history to download");
      return;
    }

    const doc = new jsPDF("landscape");
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(40, 40, 40);
    doc.text("E-Way Bill Verification History", 14, 20);
    
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);
    doc.text(`Total Records: ${ewbHistory.length}`, 14, 34);

    // Table data
    const tableData = ewbHistory.map((item, idx) => [
      idx + 1,
      item.ewb_number || "N/A",
      item.verification_data?.status || "N/A",
      item.verification_data?.valid_upto || "N/A",
      item.verification_data?.vehicle_number || "N/A",
      item.verification_data?.from_pincode || "N/A",
      item.verification_data?.to_pincode || "N/A",
      formatCurrency(item.verification_data?.total_value || 0),
      item.verified_by || "N/A",
      new Date(item.verified_at).toLocaleDateString()
    ]);

    autoTable(doc, {
      startY: 42,
      head: [["#", "EWB Number", "Status", "Valid Upto", "Vehicle", "From Pin", "To Pin", "Value", "Verified By", "Date"]],
      body: tableData,
      theme: "grid",
      headStyles: {
        fillColor: [234, 88, 12],
        textColor: 255,
        fontSize: 8,
        fontStyle: "bold"
      },
      bodyStyles: {
        fontSize: 7,
        textColor: [50, 50, 50]
      },
      alternateRowStyles: {
        fillColor: [255, 247, 237]
      },
      columnStyles: {
        0: { cellWidth: 10 },
        1: { cellWidth: 30 },
        2: { cellWidth: 20 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25 },
        5: { cellWidth: 20 },
        6: { cellWidth: 20 },
        7: { cellWidth: 25 },
        8: { cellWidth: 30 },
        9: { cellWidth: 22 }
      }
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150);
      doc.text(
        `Page ${i} of ${pageCount} | Ardhanageshwar Logistics TMS`,
        doc.internal.pageSize.width / 2,
        doc.internal.pageSize.height - 10,
        { align: "center" }
      );
    }

    doc.save(`EWay_Bill_History_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("E-Way Bill History PDF downloaded successfully");
  };

  // Download Single RC Verification as PDF
  const downloadSingleRcPDF = (rcDetails) => {
    if (!rcDetails || !rcDetails.success) {
      toast.error("No RC data to download");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(59, 130, 246);
    doc.rect(0, 0, 210, 35, "F");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text("Vehicle RC Verification Report", 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    let yPos = 45;
    
    // Vehicle Number Box
    doc.setFillColor(240, 253, 244);
    doc.rect(14, yPos, 182, 20, "F");
    doc.setDrawColor(34, 197, 94);
    doc.rect(14, yPos, 182, 20, "S");
    doc.setFontSize(12);
    doc.setTextColor(22, 163, 74);
    doc.text("✓ RC VERIFIED SUCCESSFULLY", 20, yPos + 8);
    doc.setFontSize(16);
    doc.setTextColor(40, 40, 40);
    doc.text(`Vehicle: ${rcDetails.vehicle_number}`, 20, yPos + 16);
    
    yPos += 30;

    // Owner & Registration Details
    doc.setFontSize(12);
    doc.setTextColor(59, 130, 246);
    doc.text("Owner & Registration Details", 14, yPos);
    yPos += 8;

    const ownerData = [
      ["Owner Name", rcDetails.owner_name || "N/A"],
      ["Owner Type", rcDetails.owner_type || "N/A"],
      ["Registration Date", rcDetails.registration_date || "N/A"],
      ["Valid Upto", rcDetails.registration_valid_upto || "N/A"],
      ["RC Status", rcDetails.rc_status || "ACTIVE"],
      ["Address", (rcDetails.present_address || "N/A").substring(0, 50)]
    ];

    autoTable(doc, {
      startY: yPos,
      body: ownerData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 45 }, 1: { cellWidth: 140 } }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Vehicle Details
    doc.setFontSize(12);
    doc.setTextColor(147, 51, 234);
    doc.text("Vehicle Details", 14, yPos);
    yPos += 8;

    const vehicleData = [
      ["Vehicle Class", rcDetails.vehicle_class || "N/A"],
      ["Maker", rcDetails.maker || "N/A"],
      ["Model", rcDetails.model || "N/A"],
      ["Fuel Type", rcDetails.fuel_type || "N/A"],
      ["Color", rcDetails.color || "N/A"],
      ["Body Type", rcDetails.body_type || "N/A"],
      ["Chassis No.", rcDetails.chassis_number || "N/A"],
      ["Engine No.", rcDetails.engine_number || "N/A"],
      ["GVW (kg)", rcDetails.gross_weight || "N/A"]
    ];

    autoTable(doc, {
      startY: yPos,
      body: vehicleData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 45 }, 1: { cellWidth: 140 } }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Validity Details
    doc.setFontSize(12);
    doc.setTextColor(234, 179, 8);
    doc.text("Validity & Insurance", 14, yPos);
    yPos += 8;

    const validityData = [
      ["Fitness Upto", rcDetails.fitness_upto || "N/A"],
      ["Insurance Upto", rcDetails.insurance_upto || "N/A"],
      ["Insurance Company", rcDetails.insurance_company || "N/A"],
      ["Tax Upto", rcDetails.tax_upto || "N/A"],
      ["PUCC Upto", rcDetails.pucc_upto || "N/A"],
      ["Permit Type", rcDetails.permit_type || "N/A"],
      ["Permit Valid Upto", rcDetails.permit_valid_upto || "N/A"],
      ["Financer", rcDetails.financer || "N/A"]
    ];

    autoTable(doc, {
      startY: yPos,
      body: validityData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 45 }, 1: { cellWidth: 140 } }
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text("Source: VAHAN Database via fastagtracking.com | Ardhanageshwar Logistics TMS", 14, 285);

    doc.save(`RC_${rcDetails.vehicle_number}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("RC Report PDF downloaded");
  };

  // Download Single E-Way Bill as PDF
  const downloadSingleEwbPDF = (ewbDetails) => {
    if (!ewbDetails || !ewbDetails.success) {
      toast.error("No E-Way Bill data to download");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFillColor(234, 88, 12);
    doc.rect(0, 0, 210, 35, "F");
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text("E-Way Bill Verification Report", 14, 20);
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 28);

    let yPos = 45;
    
    // EWB Number Box
    const statusColor = ewbDetails.status === "ACTIVE" ? [34, 197, 94] : [239, 68, 68];
    doc.setFillColor(statusColor[0], statusColor[1], statusColor[2], 0.1);
    doc.rect(14, yPos, 182, 20, "F");
    doc.setDrawColor(...statusColor);
    doc.rect(14, yPos, 182, 20, "S");
    doc.setFontSize(12);
    doc.setTextColor(...statusColor);
    doc.text(`STATUS: ${ewbDetails.status}`, 20, yPos + 8);
    doc.setFontSize(14);
    doc.setTextColor(40, 40, 40);
    doc.text(`E-Way Bill No: ${ewbDetails.ewb_number}`, 20, yPos + 16);
    doc.text(`Valid Until: ${ewbDetails.valid_upto || "N/A"}`, 120, yPos + 16);
    
    yPos += 30;

    // Transport Details
    doc.setFontSize(12);
    doc.setTextColor(147, 51, 234);
    doc.text("Transport Details", 14, yPos);
    yPos += 8;

    const transportData = [
      ["Vehicle Number", ewbDetails.vehicle_number || "N/A"],
      ["Transport Mode", ewbDetails.transport_mode_desc || "N/A"],
      ["Distance (KM)", ewbDetails.distance_km || "N/A"],
      ["Transporter ID", ewbDetails.transporter_id || "N/A"],
      ["Transporter Name", ewbDetails.transporter_name || "N/A"]
    ];

    autoTable(doc, {
      startY: yPos,
      body: transportData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 45 }, 1: { cellWidth: 140 } }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // From & To Details
    doc.setFontSize(12);
    doc.setTextColor(59, 130, 246);
    doc.text("Consignor (From)", 14, yPos);
    doc.setTextColor(34, 197, 94);
    doc.text("Consignee (To)", 110, yPos);
    yPos += 8;

    // Two column layout for From/To
    const fromToData = [
      [ewbDetails.from_gstin || "N/A", "GSTIN", ewbDetails.to_gstin || "N/A"],
      [ewbDetails.from_trade_name || "N/A", "Trade Name", ewbDetails.to_trade_name || "N/A"],
      [ewbDetails.from_place || "N/A", "Place", ewbDetails.to_place || "N/A"],
      [ewbDetails.from_pincode || "N/A", "Pincode", ewbDetails.to_pincode || "N/A"]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [["From", "Field", "To"]],
      body: fromToData,
      theme: "grid",
      headStyles: { fillColor: [229, 231, 235], textColor: [50, 50, 50], fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 3 },
      columnStyles: { 0: { cellWidth: 60 }, 1: { cellWidth: 30, halign: "center", fontStyle: "bold" }, 2: { cellWidth: 60 } }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // GST & Value Details
    doc.setFontSize(12);
    doc.setTextColor(234, 179, 8);
    doc.text("GST & Value Details", 14, yPos);
    yPos += 8;

    const gstData = [
      ["Document No.", ewbDetails.doc_number || "N/A"],
      ["Document Date", ewbDetails.doc_date || "N/A"],
      ["HSN Code", ewbDetails.hsn_code || "N/A"],
      ["CGST", formatCurrency(ewbDetails.cgst_amount || 0)],
      ["SGST", formatCurrency(ewbDetails.sgst_amount || 0)],
      ["IGST", formatCurrency(ewbDetails.igst_amount || 0)],
      ["CESS", formatCurrency(ewbDetails.cess_amount || 0)],
      ["Total Invoice Value", formatCurrency(ewbDetails.total_value || 0)]
    ];

    autoTable(doc, {
      startY: yPos,
      body: gstData,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: 3 },
      columnStyles: { 0: { fontStyle: "bold", textColor: [100, 100, 100], cellWidth: 45 }, 1: { cellWidth: 140 } }
    });

    // Footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text("Source: GST Portal via fastagtracking.com | Ardhanageshwar Logistics TMS", 14, 285);

    doc.save(`EWB_${ewbDetails.ewb_number}_${new Date().toISOString().split('T')[0]}.pdf`);
    toast.success("E-Way Bill Report PDF downloaded");
  };

  const handleRegisterSIM = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/sim-tracking`, simForm);
      toast.success("SIM registered successfully");
      setSimDialogOpen(false);
      setSimForm({ vehicle_number: "", sim_number: "", imei_number: "", provider: "airtel", validity_date: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to register SIM");
    }
  };

  const handleRegisterFastag = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/fastag`, fastagForm);
      toast.success("FASTag registered successfully");
      setFastagDialogOpen(false);
      setFastagForm({ vehicle_number: "", fastag_id: "", bank_name: "ICICI", vehicle_class: "VC10", linked_mobile: "", balance: 1000 });
      fetchData();
    } catch (error) {
      toast.error("Failed to register FASTag");
    }
  };

  const handleSimulateJourney = async (fastag) => {
    try {
      await axios.post(`${API_URL}/fastag/simulate-journey`, null, {
        params: {
          vehicle_number: fastag.vehicle_number,
          fastag_id: fastag.fastag_id
        }
      });
      toast.success("Journey simulated with toll transactions");
      fetchData();
    } catch (error) {
      toast.error("Failed to simulate journey");
    }
  };

  const viewJourney = async (vehicleNumber) => {
    try {
      const response = await axios.get(`${API_URL}/fastag/vehicle-journey/${vehicleNumber}`);
      setSelectedJourney(response.data);
      if (response.data.journey_points?.length > 0) {
        setMapCenter([response.data.journey_points[0].lat, response.data.journey_points[0].lng]);
      }
      setJourneyDialogOpen(true);
    } catch (error) {
      toast.error("Failed to load journey");
    }
  };

  const updateSimLocation = async (simId) => {
    // Simulate random location update for testing
    const lat = 20.5937 + (Math.random() - 0.5) * 10;
    const lng = 78.9629 + (Math.random() - 0.5) * 10;
    const speed = Math.floor(Math.random() * 80) + 20;
    
    try {
      await axios.put(`${API_URL}/sim-tracking/${simId}/location`, null, {
        params: { latitude: lat, longitude: lng, speed, signal_strength: Math.floor(Math.random() * 30) + 70 }
      });
      toast.success("Location updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update location");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active": return "bg-green-500/20 text-green-400 border-green-500/50";
      case "inactive": case "blacklisted": return "bg-red-500/20 text-red-400 border-red-500/50";
      case "suspended": case "low_balance": return "bg-yellow-500/20 text-yellow-400 border-yellow-500/50";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/50";
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);
  };

  return (
    <div className="space-y-6" data-testid="sim-fastag-tracking-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold">SIM & FASTag Tracking</h1>
          <p className="text-muted-foreground">Track vehicles via SIM cards and toll transactions</p>
        </div>
        <Button onClick={fetchData} variant="outline">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-500/20 rounded-lg">
                <Smartphone className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{sims.length}</p>
                <p className="text-xs text-muted-foreground">Active SIMs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <CreditCard className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{fastags.length}</p>
                <p className="text-xs text-muted-foreground">FASTags</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-500/20 rounded-lg">
                <Route className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{tollTransactions.length}</p>
                <p className="text-xs text-muted-foreground">Toll Transactions</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <IndianRupee className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(tollTransactions.reduce((sum, t) => sum + (t.amount || 0), 0))}</p>
                <p className="text-xs text-muted-foreground">Total Toll Paid</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-secondary/50">
          <TabsTrigger value="sim" data-testid="tab-sim">
            <Smartphone className="w-4 h-4 mr-2" />
            SIM Tracking
          </TabsTrigger>
          <TabsTrigger value="fastag" data-testid="tab-fastag">
            <CreditCard className="w-4 h-4 mr-2" />
            FASTag
          </TabsTrigger>
          <TabsTrigger value="rc" data-testid="tab-rc">
            <Car className="w-4 h-4 mr-2" />
            RC Verify
          </TabsTrigger>
          <TabsTrigger value="eway" data-testid="tab-eway">
            <FileText className="w-4 h-4 mr-2" />
            E-Way Bill
          </TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">
            <Route className="w-4 h-4 mr-2" />
            Toll Txns
          </TabsTrigger>
        </TabsList>

        {/* SIM Tracking Tab */}
        <TabsContent value="sim" className="space-y-4">
          <div className="flex justify-end">
            <Button onClick={() => setSimDialogOpen(true)} className="bg-primary" data-testid="register-sim-btn">
              <Plus className="w-4 h-4 mr-2" />
              Register SIM
            </Button>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Smartphone className="w-5 h-5" />
                Registered SIM Cards (Airtel IoT)
              </CardTitle>
              <CardDescription>Track vehicles via SIM-based location</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground">SIM Number</TableHead>
                    <TableHead className="text-muted-foreground">IMEI</TableHead>
                    <TableHead className="text-muted-foreground">Provider</TableHead>
                    <TableHead className="text-muted-foreground">Last Location</TableHead>
                    <TableHead className="text-muted-foreground">Signal</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sims.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                        No SIM cards registered
                      </TableCell>
                    </TableRow>
                  ) : (
                    sims.map((sim) => (
                      <TableRow key={sim.id} className="border-border">
                        <TableCell className="font-mono font-medium">{sim.vehicle_number}</TableCell>
                        <TableCell>{sim.sim_number}</TableCell>
                        <TableCell className="font-mono text-sm">{sim.imei_number || "N/A"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{sim.provider}</Badge>
                        </TableCell>
                        <TableCell>
                          {sim.last_location ? (
                            <div className="text-xs">
                              <p>{sim.last_location.lat?.toFixed(4)}, {sim.last_location.lng?.toFixed(4)}</p>
                              <p className="text-muted-foreground">{sim.last_location.speed} km/h</p>
                            </div>
                          ) : (
                            <span className="text-muted-foreground">No data</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {sim.last_location?.signal_strength && (
                            <div className="flex items-center gap-1">
                              <Signal className="w-3 h-3" />
                              {sim.last_location.signal_strength}%
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusBadge(sim.status)}>
                            {sim.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => updateSimLocation(sim.id)} data-testid={`update-sim-${sim.id}`}>
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Update
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* FASTag Tab */}
        <TabsContent value="fastag" className="space-y-4">
          {/* Live FASTag Tracking Section */}
          <Card className="bg-gradient-to-r from-blue-900/30 to-purple-900/30 border-blue-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-400">
                <Zap className="w-5 h-5" />
                Live FASTag Tracking
              </CardTitle>
              <CardDescription>Track any vehicle via toll plaza crossings (Powered by fastagtracking.com)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter Vehicle Number (e.g., HR55AT4106)"
                      value={liveVehicleNumber}
                      onChange={(e) => setLiveVehicleNumber(e.target.value.toUpperCase())}
                      className="bg-secondary/50 font-mono"
                      data-testid="live-vehicle-input"
                    />
                    <Button 
                      onClick={() => fetchLiveTracking(liveVehicleNumber)}
                      disabled={liveTrackingLoading || !liveVehicleNumber}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="live-track-btn"
                    >
                      {liveTrackingLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4 mr-2" />
                          Track
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* Live Tracking Results */}
              {liveTrackingData && (
                <div className="mt-4 space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <div className="bg-secondary/30 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Vehicle</p>
                      <p className="font-mono font-bold text-lg">{liveTrackingData.vehicle_number}</p>
                    </div>
                    <div className="bg-secondary/30 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Total Toll Paid</p>
                      <p className="font-bold text-lg text-green-400">{formatCurrency(liveTrackingData.total_toll_paid)}</p>
                    </div>
                    <div className="bg-secondary/30 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Toll Crossings</p>
                      <p className="font-bold text-lg text-blue-400">{liveTrackingData.toll_count}</p>
                    </div>
                    <div className="bg-secondary/30 p-3 rounded-lg text-center">
                      <p className="text-xs text-muted-foreground">Data Source</p>
                      <p className="font-bold text-xs text-purple-400">{liveTrackingData.source}</p>
                    </div>
                  </div>

                  {/* Recent Toll Crossings */}
                  {liveTrackingData.transactions?.length > 0 && (
                    <div className="bg-secondary/20 rounded-lg p-4">
                      <h4 className="font-medium mb-3 flex items-center gap-2">
                        <Route className="w-4 h-4" />
                        Recent Toll Crossings
                      </h4>
                      <div className="max-h-64 overflow-y-auto space-y-2">
                        {liveTrackingData.transactions.slice(0, 10).map((tx, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-secondary/30 rounded-lg text-sm">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <MapPin className="w-4 h-4 text-blue-400" />
                              </div>
                              <div>
                                <p className="font-medium">{tx.toll_plaza_name}</p>
                                <p className="text-xs text-muted-foreground">{tx.transaction_time}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-bold text-green-400">{formatCurrency(tx.amount)}</p>
                              <p className="text-xs text-muted-foreground">{tx.vehicle_class}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* View on Map Button */}
                  {liveTrackingData.journey_points?.length > 0 && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => {
                        setSelectedJourney({
                          vehicle_number: liveTrackingData.vehicle_number,
                          journey_points: liveTrackingData.journey_points,
                          transactions: liveTrackingData.transactions
                        });
                        setJourneyDialogOpen(true);
                      }}
                    >
                      <Map className="w-4 h-4 mr-2" />
                      View Route on Map ({liveTrackingData.journey_points.length} locations)
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button onClick={() => setFastagDialogOpen(true)} className="bg-primary" data-testid="register-fastag-btn">
              <Plus className="w-4 h-4 mr-2" />
              Register FASTag
            </Button>
          </div>

          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Registered FASTags (NPCI/NETC)
              </CardTitle>
              <CardDescription>Track vehicles via toll transactions</CardDescription>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground">FASTag ID</TableHead>
                    <TableHead className="text-muted-foreground">Bank</TableHead>
                    <TableHead className="text-muted-foreground">Class</TableHead>
                    <TableHead className="text-muted-foreground">Balance</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {fastags.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No FASTags registered
                      </TableCell>
                    </TableRow>
                  ) : (
                    fastags.map((fastag) => (
                      <TableRow key={fastag.id} className="border-border">
                        <TableCell className="font-mono font-medium">{fastag.vehicle_number}</TableCell>
                        <TableCell className="font-mono text-sm">{fastag.fastag_id}</TableCell>
                        <TableCell>{fastag.bank_name}</TableCell>
                        <TableCell>{fastag.vehicle_class}</TableCell>
                        <TableCell className={fastag.balance < 200 ? "text-red-400" : "text-green-400"}>
                          {formatCurrency(fastag.balance || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusBadge(fastag.tag_status)}>
                            {fastag.tag_status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" onClick={() => handleSimulateJourney(fastag)} data-testid={`simulate-journey-${fastag.id}`}>
                              <PlayCircle className="w-3 h-3 mr-1" />
                              Simulate
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => viewJourney(fastag.vehicle_number)} data-testid={`view-journey-${fastag.id}`}>
                              <Eye className="w-3 h-3 mr-1" />
                              Journey
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* FASTag Trips Section */}
          <Card className="bg-gradient-to-r from-amber-900/30 to-orange-900/30 border-amber-500/30">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2 text-amber-400">
                  <Navigation className="w-5 h-5" />
                  FASTag Trips
                </CardTitle>
                <CardDescription>Create and track trips with toll data</CardDescription>
              </div>
              <Button 
                onClick={() => setTripDialogOpen(true)} 
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="create-trip-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Trip
              </Button>
            </CardHeader>
            <CardContent>
              {fastagTrips.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Navigation className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No trips created yet</p>
                  <p className="text-sm">Create a trip to track toll expenses</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-border hover:bg-transparent">
                      <TableHead className="text-muted-foreground">Vehicle</TableHead>
                      <TableHead className="text-muted-foreground">Route</TableHead>
                      <TableHead className="text-muted-foreground">Driver</TableHead>
                      <TableHead className="text-muted-foreground">Start Date</TableHead>
                      <TableHead className="text-muted-foreground">Toll Crossings</TableHead>
                      <TableHead className="text-muted-foreground">Toll Amount</TableHead>
                      <TableHead className="text-muted-foreground">Status</TableHead>
                      <TableHead className="text-muted-foreground">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fastagTrips.map((trip) => (
                      <TableRow key={trip.id} className="border-border">
                        <TableCell className="font-mono font-medium">{trip.vehicle_number}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-sm">
                            <MapPinned className="w-3 h-3 text-green-400" />
                            <span className="truncate max-w-[80px]">{trip.from_location}</span>
                            <ArrowRight className="w-3 h-3 text-muted-foreground" />
                            <MapPinned className="w-3 h-3 text-red-400" />
                            <span className="truncate max-w-[80px]">{trip.to_location}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {trip.driver_name ? (
                            <div className="text-sm">
                              <p>{trip.driver_name}</p>
                              {trip.driver_phone && (
                                <p className="text-xs text-muted-foreground">{trip.driver_phone}</p>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {new Date(trip.start_date).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="bg-blue-500/20 text-blue-400">
                            {trip.toll_crossings || 0} tolls
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium text-green-400">
                          {formatCurrency(trip.actual_toll_amount || 0)}
                        </TableCell>
                        <TableCell>
                          <Badge className={
                            trip.status === "active" ? "bg-green-500" :
                            trip.status === "completed" ? "bg-blue-500" :
                            "bg-gray-500"
                          }>
                            {trip.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleUpdateTripToll(trip.id)}
                              title="Fetch latest toll data"
                              data-testid={`update-toll-${trip.id}`}
                            >
                              <RefreshCw className="w-3 h-3" />
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleViewTrip(trip)}
                              data-testid={`view-trip-${trip.id}`}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {trip.status === "active" && (
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="text-green-400 border-green-500/50"
                                onClick={() => handleCompleteTrip(trip.id)}
                                data-testid={`complete-trip-${trip.id}`}
                              >
                                <CheckCircle className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* RC Verification Tab */}
        <TabsContent value="rc" className="space-y-4">
          {/* RC Verification Search */}
          <Card className="bg-gradient-to-r from-green-900/30 to-emerald-900/30 border-green-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-400">
                <Car className="w-5 h-5" />
                Vehicle RC Verification
              </CardTitle>
              <CardDescription>Verify vehicle registration certificate details via VAHAN database</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter Vehicle Number (e.g., HR55AT4106)"
                      value={rcVehicleNumber}
                      onChange={(e) => setRcVehicleNumber(e.target.value.toUpperCase())}
                      className="bg-secondary/50 font-mono"
                      data-testid="rc-vehicle-input"
                    />
                    <Button 
                      onClick={verifyRC}
                      disabled={rcLoading || !rcVehicleNumber}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid="rc-verify-btn"
                    >
                      {rcLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Verify RC
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* RC Verification Results */}
              {rcData && (
                <div className="mt-4 space-y-4">
                  {rcData.success ? (
                    <>
                      {/* Success Header with Download Button */}
                      <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="font-medium text-green-400">RC Verified Successfully</span>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => downloadSingleRcPDF(rcData)}
                          className="border-green-500/50 text-green-400 hover:bg-green-500/20"
                          data-testid="download-single-rc-pdf"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>

                      {/* Vehicle Summary */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Vehicle Number</p>
                          <p className="font-mono font-bold text-lg">{rcData.vehicle_number}</p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Owner</p>
                          <p className="font-bold text-sm truncate">{rcData.owner_name || "N/A"}</p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Vehicle Class</p>
                          <p className="font-bold text-sm">{rcData.vehicle_class || "N/A"}</p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">RC Status</p>
                          <Badge className={rcData.rc_status === "ACTIVE" ? "bg-green-500" : "bg-yellow-500"}>
                            {rcData.rc_status || "ACTIVE"}
                          </Badge>
                        </div>
                      </div>

                      {/* Detailed RC Info */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Vehicle Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-blue-400">
                            <Truck className="w-4 h-4" />
                            Vehicle Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Maker</span>
                              <span className="font-medium">{rcData.maker || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Model</span>
                              <span className="font-medium">{rcData.model || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Fuel Type</span>
                              <span className="font-medium">{rcData.fuel_type || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Color</span>
                              <span className="font-medium">{rcData.color || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Body Type</span>
                              <span className="font-medium">{rcData.body_type || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Manufacturing</span>
                              <span className="font-medium">{rcData.manufacturing_date || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Technical Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-400">
                            <Shield className="w-4 h-4" />
                            Technical Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Chassis No.</span>
                              <span className="font-mono text-xs">{rcData.chassis_number || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Engine No.</span>
                              <span className="font-mono text-xs">{rcData.engine_number || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GVW (Gross Weight)</span>
                              <span className="font-medium">{rcData.gross_weight || "N/A"} kg</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Unladen Weight</span>
                              <span className="font-medium">{rcData.unladen_weight || "N/A"} kg</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Seating Capacity</span>
                              <span className="font-medium">{rcData.seating_capacity || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Cylinders</span>
                              <span className="font-medium">{rcData.cylinders || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        {/* Validity Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-yellow-400">
                            <Calendar className="w-4 h-4" />
                            Validity & Insurance
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Registration Date</span>
                              <span className="font-medium">{rcData.registration_date || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Valid Upto</span>
                              <span className="font-medium">{rcData.registration_valid_upto || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Fitness Upto</span>
                              <span className={`font-medium ${rcData.fitness_upto && new Date(rcData.fitness_upto) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                                {rcData.fitness_upto || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Insurance Upto</span>
                              <span className={`font-medium ${rcData.insurance_upto && new Date(rcData.insurance_upto) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                                {rcData.insurance_upto || "N/A"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Tax Upto</span>
                              <span className="font-medium">{rcData.tax_upto || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">PUCC Upto</span>
                              <span className={`font-medium ${rcData.pucc_upto && new Date(rcData.pucc_upto) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                                {rcData.pucc_upto || "N/A"}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Permit & Financer */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-orange-400">
                            <Building2 className="w-4 h-4" />
                            Permit & Financer
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Permit Type</span>
                              <span className="font-medium">{rcData.permit_type || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Permit Valid Upto</span>
                              <span className="font-medium">{rcData.permit_valid_upto || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Insurance Company</span>
                              <span className="font-medium text-xs">{rcData.insurance_company || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Financer</span>
                              <span className="font-medium text-xs">{rcData.financer || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Vehicle Age</span>
                              <span className="font-medium">{rcData.vehicle_age_years && typeof rcData.vehicle_age_years === 'number' ? `${rcData.vehicle_age_years.toFixed(1)} years` : (rcData.vehicle_age || "N/A")}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Address */}
                      {rcData.present_address && (
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <User className="w-4 h-4" />
                            Owner Address
                          </h4>
                          <p className="text-sm text-muted-foreground">{rcData.present_address}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-center gap-2 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">{rcData.error || "RC verification failed"}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* RC Verification History */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent RC Verifications
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={downloadRcHistoryPDF}
                disabled={rcHistory.length === 0}
                data-testid="download-rc-history-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Time</TableHead>
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground">Owner</TableHead>
                    <TableHead className="text-muted-foreground">Type</TableHead>
                    <TableHead className="text-muted-foreground">Verified By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rcHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8 text-muted-foreground">
                        No RC verifications yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    rcHistory.map((item, idx) => (
                      <TableRow key={idx} className="border-border">
                        <TableCell className="text-sm">
                          {new Date(item.verified_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono font-medium">{item.vehicle_number}</TableCell>
                        <TableCell>{item.verification_data?.owner_name || "N/A"}</TableCell>
                        <TableCell>{item.verification_data?.vehicle_class || "N/A"}</TableCell>
                        <TableCell>{item.verified_by}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* E-Way Bill Verification Tab */}
        <TabsContent value="eway" className="space-y-4">
          {/* E-Way Bill Search */}
          <Card className="bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-500/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-orange-400">
                <FileText className="w-5 h-5" />
                E-Way Bill Verification
              </CardTitle>
              <CardDescription>Verify E-Way Bill details from GST Portal</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <div className="flex gap-2">
                    <Input
                      placeholder="Enter E-Way Bill Number (e.g., 741625836705)"
                      value={ewbNumber}
                      onChange={(e) => setEwbNumber(e.target.value.replace(/\D/g, ''))}
                      className="bg-secondary/50 font-mono"
                      data-testid="ewb-number-input"
                    />
                    <Button 
                      onClick={verifyEwayBill}
                      disabled={ewbLoading || !ewbNumber}
                      className="bg-orange-600 hover:bg-orange-700"
                      data-testid="ewb-verify-btn"
                    >
                      {ewbLoading ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <FileText className="w-4 h-4 mr-2" />
                          Verify EWB
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              {/* E-Way Bill Results */}
              {ewbData && (
                <div className="mt-4 space-y-4">
                  {ewbData.success ? (
                    <>
                      {/* Success Header with Download Button */}
                      <div className="flex items-center justify-between p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-5 h-5 text-green-400" />
                          <span className="font-medium text-green-400">E-Way Bill Verified</span>
                          <Badge className={
                            ewbData.status === "ACTIVE" || ewbData.status === "Active" ? "bg-green-500" :
                            ewbData.status === "EXPIRED" ? "bg-red-500" :
                            ewbData.status === "CANCELLED" ? "bg-gray-500" : "bg-yellow-500"
                          }>
                            {ewbData.status}
                          </Badge>
                        </div>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => downloadSingleEwbPDF(ewbData)}
                          className="border-orange-500/50 text-orange-400 hover:bg-orange-500/20"
                          data-testid="download-single-ewb-pdf"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download PDF
                        </Button>
                      </div>

                      {/* EWB Summary */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">EWB Number</p>
                          <p className="font-mono font-bold text-sm">{ewbData.ewb_number}</p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Total Value</p>
                          <p className="font-bold text-lg text-green-400">{formatCurrency(ewbData.total_value || 0)}</p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Valid Until</p>
                          <p className={`font-bold text-sm ${ewbData.valid_upto && new Date(ewbData.valid_upto) < new Date() ? 'text-red-400' : 'text-green-400'}`}>
                            {ewbData.valid_upto || "N/A"}
                          </p>
                        </div>
                        <div className="bg-secondary/30 p-3 rounded-lg text-center">
                          <p className="text-xs text-muted-foreground">Vehicle</p>
                          <p className="font-mono font-bold text-sm">{ewbData.vehicle_number || "N/A"}</p>
                        </div>
                      </div>

                      {/* From - To Section */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* From Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-blue-400">
                            <Building2 className="w-4 h-4" />
                            From (Consignor)
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GSTIN</span>
                              <span className="font-mono text-xs">{ewbData.from_gstin || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Trade Name</span>
                              <span className="font-medium text-xs">{ewbData.from_trade_name || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Place</span>
                              <span className="font-medium">{ewbData.from_place || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Pincode</span>
                              <span className="font-medium">{ewbData.from_pincode || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        {/* To Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-green-400">
                            <ArrowRight className="w-4 h-4" />
                            To (Consignee)
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">GSTIN</span>
                              <span className="font-mono text-xs">{ewbData.to_gstin || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Trade Name</span>
                              <span className="font-medium text-xs">{ewbData.to_trade_name || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Place</span>
                              <span className="font-medium">{ewbData.to_place || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Pincode</span>
                              <span className="font-medium">{ewbData.to_pincode || "N/A"}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Transport & GST Details */}
                      <div className="grid md:grid-cols-2 gap-4">
                        {/* Transport Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-400">
                            <Truck className="w-4 h-4" />
                            Transport Details
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Transporter ID</span>
                              <span className="font-mono text-xs">{ewbData.transporter_id || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Transporter Name</span>
                              <span className="font-medium text-xs">{ewbData.transporter_name || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Mode</span>
                              <span className="font-medium">{ewbData.transport_mode_desc || ewbData.transport_mode || "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Distance</span>
                              <span className="font-medium">{ewbData.distance_km ? `${ewbData.distance_km} KM` : "N/A"}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">Vehicle Type</span>
                              <span className="font-medium">{ewbData.vehicle_type || "N/A"}</span>
                            </div>
                          </div>
                        </div>

                        {/* GST Details */}
                        <div className="bg-secondary/20 rounded-lg p-4">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-yellow-400">
                            <IndianRupee className="w-4 h-4" />
                            GST & Value
                          </h4>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">CGST</span>
                              <span className="font-medium">{formatCurrency(ewbData.cgst_amount || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">SGST</span>
                              <span className="font-medium">{formatCurrency(ewbData.sgst_amount || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">IGST</span>
                              <span className="font-medium">{formatCurrency(ewbData.igst_amount || 0)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-muted-foreground">CESS</span>
                              <span className="font-medium">{formatCurrency(ewbData.cess_amount || 0)}</span>
                            </div>
                            <div className="flex justify-between border-t border-border pt-2 mt-2">
                              <span className="text-muted-foreground font-medium">Total Invoice Value</span>
                              <span className="font-bold text-green-400">{formatCurrency(ewbData.total_value || 0)}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Document Details */}
                      <div className="bg-secondary/20 rounded-lg p-4">
                        <h4 className="font-medium mb-3 flex items-center gap-2">
                          <FileText className="w-4 h-4" />
                          Document Details
                        </h4>
                        <div className="grid sm:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-muted-foreground">Document No.</p>
                            <p className="font-mono font-medium">{ewbData.doc_number || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">Document Date</p>
                            <p className="font-medium">{ewbData.doc_date || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">EWB Date</p>
                            <p className="font-medium">{ewbData.ewb_date || "N/A"}</p>
                          </div>
                          <div>
                            <p className="text-muted-foreground">HSN Code</p>
                            <p className="font-mono font-medium">{ewbData.hsn_code || "N/A"}</p>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-center gap-2 p-4 bg-red-500/10 rounded-lg border border-red-500/30">
                      <XCircle className="w-5 h-5 text-red-400" />
                      <span className="text-red-400">{ewbData.error || "E-Way Bill verification failed"}</span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* E-Way Bill History */}
          <Card className="bg-card border-border">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Recent E-Way Bill Verifications
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={downloadEwbHistoryPDF}
                disabled={ewbHistory.length === 0}
                data-testid="download-ewb-history-pdf"
              >
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Time</TableHead>
                    <TableHead className="text-muted-foreground">EWB Number</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                    <TableHead className="text-muted-foreground">Value</TableHead>
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground">Verified By</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ewbHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        No E-Way Bill verifications yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    ewbHistory.map((item, idx) => (
                      <TableRow key={idx} className="border-border">
                        <TableCell className="text-sm">
                          {new Date(item.verified_at).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono font-medium">{item.ewb_number}</TableCell>
                        <TableCell>
                          <Badge className={
                            item.verification_data?.status === "ACTIVE" || item.verification_data?.status === "Active" ? "bg-green-500" :
                            item.verification_data?.status === "EXPIRED" ? "bg-red-500" : "bg-yellow-500"
                          }>
                            {item.verification_data?.status || "N/A"}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatCurrency(item.verification_data?.total_value || 0)}</TableCell>
                        <TableCell className="font-mono">{item.verification_data?.vehicle_number || "N/A"}</TableCell>
                        <TableCell>{item.verified_by}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Toll Transactions Tab */}
        <TabsContent value="transactions" className="space-y-4">
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Route className="w-5 h-5" />
                Recent Toll Transactions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="border-border hover:bg-transparent">
                    <TableHead className="text-muted-foreground">Time</TableHead>
                    <TableHead className="text-muted-foreground">Vehicle</TableHead>
                    <TableHead className="text-muted-foreground">Toll Plaza</TableHead>
                    <TableHead className="text-muted-foreground">Entry/Exit</TableHead>
                    <TableHead className="text-muted-foreground">Transaction ID</TableHead>
                    <TableHead className="text-muted-foreground text-right">Amount</TableHead>
                    <TableHead className="text-muted-foreground">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tollTransactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        No toll transactions yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    tollTransactions.map((tx) => (
                      <TableRow key={tx.id} className="border-border">
                        <TableCell className="text-sm">
                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(tx.transaction_time).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell className="font-mono font-medium">{tx.vehicle_number}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{tx.toll_plaza_name}</p>
                            {tx.location?.highway && (
                              <p className="text-xs text-muted-foreground">{tx.location.highway}, {tx.location.state}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={tx.entry_exit === "entry" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"}>
                            {tx.entry_exit}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{tx.transaction_id}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(tx.amount)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusBadge(tx.transaction_status === "success" ? "active" : tx.transaction_status)}>
                            {tx.transaction_status}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Register SIM Dialog */}
      <Dialog open={simDialogOpen} onOpenChange={setSimDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Register SIM Card
            </DialogTitle>
            <DialogDescription>Add a SIM for vehicle tracking (Airtel IoT)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegisterSIM} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number *</Label>
                <Select value={simForm.vehicle_number} onValueChange={(v) => setSimForm({...simForm, vehicle_number: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {trucks.map((truck) => (
                      <SelectItem key={truck.id} value={truck.truck_number}>{truck.truck_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>SIM Number *</Label>
                <Input
                  value={simForm.sim_number}
                  onChange={(e) => setSimForm({...simForm, sim_number: e.target.value})}
                  placeholder="9876543210"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>IMEI Number</Label>
                <Input
                  value={simForm.imei_number}
                  onChange={(e) => setSimForm({...simForm, imei_number: e.target.value})}
                  placeholder="Optional"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Provider</Label>
                <Select value={simForm.provider} onValueChange={(v) => setSimForm({...simForm, provider: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="airtel">Airtel IoT</SelectItem>
                    <SelectItem value="jio">Jio IoT</SelectItem>
                    <SelectItem value="bsnl">BSNL M2M</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Validity Date</Label>
              <Input
                type="date"
                value={simForm.validity_date}
                onChange={(e) => setSimForm({...simForm, validity_date: e.target.value})}
                className="bg-secondary/50"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setSimDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Register SIM</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Register FASTag Dialog */}
      <Dialog open={fastagDialogOpen} onOpenChange={setFastagDialogOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5" />
              Register FASTag
            </DialogTitle>
            <DialogDescription>Add a FASTag for toll tracking (NPCI/NETC)</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleRegisterFastag} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle Number *</Label>
                <Select value={fastagForm.vehicle_number} onValueChange={(v) => setFastagForm({...fastagForm, vehicle_number: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {trucks.map((truck) => (
                      <SelectItem key={truck.id} value={truck.truck_number}>{truck.truck_number}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>FASTag ID *</Label>
                <Input
                  value={fastagForm.fastag_id}
                  onChange={(e) => setFastagForm({...fastagForm, fastag_id: e.target.value.toUpperCase()})}
                  placeholder="TAG123456789"
                  className="bg-secondary/50"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bank</Label>
                <Select value={fastagForm.bank_name} onValueChange={(v) => setFastagForm({...fastagForm, bank_name: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ICICI">ICICI Bank</SelectItem>
                    <SelectItem value="HDFC">HDFC Bank</SelectItem>
                    <SelectItem value="SBI">SBI</SelectItem>
                    <SelectItem value="Paytm">Paytm Payments Bank</SelectItem>
                    <SelectItem value="Axis">Axis Bank</SelectItem>
                    <SelectItem value="IDFC">IDFC First Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vehicle Class</Label>
                <Select value={fastagForm.vehicle_class} onValueChange={(v) => setFastagForm({...fastagForm, vehicle_class: v})}>
                  <SelectTrigger className="bg-secondary/50">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="VC4">VC4 - Car/Jeep/Van</SelectItem>
                    <SelectItem value="VC5">VC5 - LCV</SelectItem>
                    <SelectItem value="VC6">VC6 - Bus/Truck (2 Axle)</SelectItem>
                    <SelectItem value="VC7">VC7 - Bus/Truck (3 Axle)</SelectItem>
                    <SelectItem value="VC10">VC10 - Multi-Axle (4-6)</SelectItem>
                    <SelectItem value="VC12">VC12 - Heavy Vehicle (7+ Axle)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Linked Mobile</Label>
                <Input
                  value={fastagForm.linked_mobile}
                  onChange={(e) => setFastagForm({...fastagForm, linked_mobile: e.target.value})}
                  placeholder="9876543210"
                  className="bg-secondary/50"
                />
              </div>
              <div className="space-y-2">
                <Label>Initial Balance</Label>
                <Input
                  type="number"
                  value={fastagForm.balance}
                  onChange={(e) => setFastagForm({...fastagForm, balance: parseFloat(e.target.value)})}
                  className="bg-secondary/50"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setFastagDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-primary">Register FASTag</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Create Trip Dialog */}
      <Dialog open={tripDialogOpen} onOpenChange={setTripDialogOpen}>
        <DialogContent className="bg-card border-border max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-amber-400" />
              Create FASTag Trip
            </DialogTitle>
            <DialogDescription>Track toll expenses for a specific trip</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateTrip} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Vehicle Number *</Label>
                <select
                  className="w-full px-3 py-2 bg-secondary/50 border border-input rounded-md text-sm"
                  value={tripForm.vehicle_number}
                  onChange={(e) => setTripForm({ ...tripForm, vehicle_number: e.target.value })}
                  data-testid="trip-vehicle-select"
                >
                  <option value="">Select Vehicle</option>
                  {trucks.map((truck) => (
                    <option key={truck.id} value={truck.truck_number}>
                      {truck.truck_number} - {truck.truck_type}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Driver Name</Label>
                <Input
                  placeholder="Driver Name"
                  value={tripForm.driver_name}
                  onChange={(e) => setTripForm({ ...tripForm, driver_name: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-driver-name"
                />
              </div>
              <div>
                <Label>Driver Phone</Label>
                <Input
                  placeholder="Phone Number"
                  value={tripForm.driver_phone}
                  onChange={(e) => setTripForm({ ...tripForm, driver_phone: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-driver-phone"
                />
              </div>
              <div>
                <Label>From Location *</Label>
                <Input
                  placeholder="e.g., Delhi"
                  value={tripForm.from_location}
                  onChange={(e) => setTripForm({ ...tripForm, from_location: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-from-location"
                />
              </div>
              <div>
                <Label>To Location *</Label>
                <Input
                  placeholder="e.g., Mumbai"
                  value={tripForm.to_location}
                  onChange={(e) => setTripForm({ ...tripForm, to_location: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-to-location"
                />
              </div>
              <div>
                <Label>Start Date *</Label>
                <Input
                  type="date"
                  value={tripForm.start_date}
                  onChange={(e) => setTripForm({ ...tripForm, start_date: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-start-date"
                />
              </div>
              <div>
                <Label>Expected End Date</Label>
                <Input
                  type="date"
                  value={tripForm.expected_end_date}
                  onChange={(e) => setTripForm({ ...tripForm, expected_end_date: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-end-date"
                />
              </div>
              <div>
                <Label>Expected Toll Amount (₹)</Label>
                <Input
                  type="number"
                  placeholder="0"
                  value={tripForm.expected_toll_amount}
                  onChange={(e) => setTripForm({ ...tripForm, expected_toll_amount: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-expected-toll"
                />
              </div>
              <div className="col-span-2">
                <Label>Remarks</Label>
                <Input
                  placeholder="Any notes about this trip"
                  value={tripForm.remarks}
                  onChange={(e) => setTripForm({ ...tripForm, remarks: e.target.value })}
                  className="bg-secondary/50"
                  data-testid="trip-remarks"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTripDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-amber-600 hover:bg-amber-700" disabled={tripLoading}>
                {tripLoading ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Create Trip"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Trip Detail Dialog */}
      <Dialog open={tripDetailDialogOpen} onOpenChange={setTripDetailDialogOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Navigation className="w-5 h-5 text-amber-400" />
              Trip Details
            </DialogTitle>
          </DialogHeader>
          {selectedTrip && (
            <div className="space-y-4">
              {/* Trip Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Vehicle</p>
                  <p className="font-mono font-bold">{selectedTrip.vehicle_number}</p>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Status</p>
                  <Badge className={
                    selectedTrip.status === "active" ? "bg-green-500" :
                    selectedTrip.status === "completed" ? "bg-blue-500" :
                    "bg-gray-500"
                  }>
                    {selectedTrip.status}
                  </Badge>
                </div>
                <div className="bg-secondary/30 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Total Toll</p>
                  <p className="font-bold text-green-400">{formatCurrency(selectedTrip.actual_toll_amount || 0)}</p>
                </div>
              </div>

              {/* Route Info */}
              <div className="bg-secondary/20 p-4 rounded-lg">
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <CircleDot className="w-4 h-4 text-green-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">From</p>
                        <p className="font-medium">{selectedTrip.from_location}</p>
                      </div>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-red-400" />
                      <div>
                        <p className="text-sm text-muted-foreground">To</p>
                        <p className="font-medium">{selectedTrip.to_location}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Driver & Date Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <User className="w-4 h-4 text-blue-400" />
                    <span className="text-sm text-muted-foreground">Driver</span>
                  </div>
                  <p className="font-medium">{selectedTrip.driver_name || "Not assigned"}</p>
                  {selectedTrip.driver_phone && (
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <Phone className="w-3 h-3" /> {selectedTrip.driver_phone}
                    </p>
                  )}
                </div>
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Calendar className="w-4 h-4 text-purple-400" />
                    <span className="text-sm text-muted-foreground">Schedule</span>
                  </div>
                  <p className="font-medium">Start: {new Date(selectedTrip.start_date).toLocaleDateString()}</p>
                  {selectedTrip.expected_end_date && (
                    <p className="text-sm text-muted-foreground">End: {new Date(selectedTrip.expected_end_date).toLocaleDateString()}</p>
                  )}
                </div>
              </div>

              {/* Toll Comparison */}
              {selectedTrip.expected_toll_amount > 0 && (
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Expected Toll</span>
                    <span className="font-medium">{formatCurrency(selectedTrip.expected_toll_amount)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Actual Toll</span>
                    <span className="font-medium text-green-400">{formatCurrency(selectedTrip.actual_toll_amount || 0)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-border pt-2 mt-2">
                    <span className="text-sm font-medium">Difference</span>
                    <span className={`font-bold ${(selectedTrip.actual_toll_amount || 0) > selectedTrip.expected_toll_amount ? 'text-red-400' : 'text-green-400'}`}>
                      {(selectedTrip.actual_toll_amount || 0) > selectedTrip.expected_toll_amount ? '+' : ''}
                      {formatCurrency((selectedTrip.actual_toll_amount || 0) - selectedTrip.expected_toll_amount)}
                    </span>
                  </div>
                </div>
              )}

              {/* Toll Transactions */}
              {selectedTrip.toll_transactions && selectedTrip.toll_transactions.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <Route className="w-4 h-4" />
                    Toll Transactions ({selectedTrip.toll_crossings})
                  </h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {selectedTrip.toll_transactions.map((tx, idx) => (
                      <div key={idx} className="bg-secondary/30 p-2 rounded flex justify-between items-center text-sm">
                        <div>
                          <p className="font-medium">{tx.toll_plaza}</p>
                          <p className="text-xs text-muted-foreground">
                            {tx.transaction_time ? new Date(tx.transaction_time).toLocaleString() : '-'}
                          </p>
                        </div>
                        <span className="font-mono text-green-400">{formatCurrency(tx.amount)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Remarks */}
              {selectedTrip.remarks && (
                <div className="bg-secondary/20 p-3 rounded-lg">
                  <p className="text-sm text-muted-foreground">Remarks</p>
                  <p>{selectedTrip.remarks}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-between pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  onClick={() => handleUpdateTripToll(selectedTrip.id)}
                  data-testid="trip-detail-refresh-toll"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Refresh Toll Data
                </Button>
                <div className="flex gap-2">
                  {selectedTrip.status === "active" && (
                    <>
                      <Button 
                        variant="outline" 
                        className="text-red-400 border-red-500/50 hover:bg-red-500/20"
                        onClick={() => handleCancelTrip(selectedTrip.id)}
                        data-testid="trip-detail-cancel"
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Cancel
                      </Button>
                      <Button 
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleCompleteTrip(selectedTrip.id)}
                        data-testid="trip-detail-complete"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Complete Trip
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Journey View Dialog */}
      <Dialog open={journeyDialogOpen} onOpenChange={setJourneyDialogOpen}>
        <DialogContent className="bg-card border-border max-w-4xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Route className="w-5 h-5" />
              Vehicle Journey: {selectedJourney?.vehicle_number}
            </DialogTitle>
            <DialogDescription>
              {selectedJourney && (
                <span>
                  {selectedJourney.toll_count} toll crossings | Total: {formatCurrency(selectedJourney.total_toll_paid)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {selectedJourney && (
            <div className="space-y-4">
              {/* Map */}
              <div className="h-[300px] rounded-lg overflow-hidden border border-border">
                <MapContainer
                  center={mapCenter}
                  zoom={6}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapCenter center={mapCenter} />
                  {selectedJourney.journey_points?.length > 0 && (
                    <>
                      <Polyline
                        positions={selectedJourney.journey_points.map(p => [p.lat, p.lng])}
                        color="#f59e0b"
                        weight={3}
                        dashArray="10, 10"
                      />
                      {selectedJourney.journey_points.map((point, idx) => (
                        <Marker key={idx} position={[point.lat, point.lng]} icon={tollIcon}>
                          <Popup>
                            <div className="p-1">
                              <p className="font-bold">{point.toll_plaza}</p>
                              <p className="text-sm">{formatCurrency(point.amount)}</p>
                              <p className="text-xs text-gray-500">{new Date(point.time).toLocaleString()}</p>
                            </div>
                          </Popup>
                        </Marker>
                      ))}
                    </>
                  )}
                </MapContainer>
              </div>

              {/* Journey Points List */}
              <div className="max-h-[200px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">#</TableHead>
                      <TableHead className="text-muted-foreground">Toll Plaza</TableHead>
                      <TableHead className="text-muted-foreground">Time</TableHead>
                      <TableHead className="text-muted-foreground text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedJourney.journey_points?.map((point, idx) => (
                      <TableRow key={idx} className="border-border">
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell className="font-medium">{point.toll_plaza}</TableCell>
                        <TableCell className="text-sm">{new Date(point.time).toLocaleString()}</TableCell>
                        <TableCell className="text-right">{formatCurrency(point.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setJourneyDialogOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SIMFastagTrackingPage;
